import { Upload } from "@mui/icons-material";
import { Alert, Box, FormHelperText, MenuItem, styled } from "@mui/material";
import { useFormikContext } from "formik";
import _, { uniq } from "lodash";
import { ReactElement, ReactNode, useState } from "react";

import { parseTextAndUrlSegments, replaceURLs } from "@stw/extract-urls";
import LiquidTemplate, {
  LiquidTemplateParseResult,
} from "@stw/liquid-template";
import { isValidUrl } from "@stw/mime-types";
import { EncodingStats, getEncodingStats } from "@stw/sms-text-encoding";

import {
  CreateCampaignContactFieldMutation,
  CreateCampaignContactFieldMutationVariables,
  PersonalizedGifStatus,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { FormikSelect } from "~frontend/components/forms";
import { FormikScriptField } from "~frontend/components/forms/FormScriptField";
import {
  MediaFieldValue,
  MediaPreview,
} from "~frontend/components/forms/FormikMediaField";
import { CampaignPersonalizedGifPreview } from "~frontend/components/personalizedGifs/CampaignPersonalizedGifPreview";
import {
  CampaignPersonalizedGif,
  PersonalizedGif,
  VALID_PERSONALIZED_GIF_MESSAGING_STRATEGIES,
} from "~frontend/components/personalizedGifs/types";
import {
  CREATE_CAMPAIGN_CONTACT_FIELD,
  createCampaignContactFieldUpdater,
} from "~frontend/graphql/campaigns";
import { useTenant } from "~frontend/providers/LoggedInUserProvider";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { useSubaccountAdminConfig } from "~frontend/providers/SubaccountProvider";
import { formatContactData } from "~frontend/util/contactFields";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import {
  MEDIA_FILE_MAX_SIZE_BYTES,
  MEDIA_FILE_MIME_TYPES,
} from "~frontend/util/mediaFiles";
import useMutation from "~frontend/util/useMutation";

import { CampaignForEditor } from "../types";
import LinkShortenerSettings from "./LinkShortenerSettings";
import { ScriptPreview } from "./ScriptPreview";
import { SegmentEstimate, SegmentEstimateDetail } from "./SegmentEstimate";
import { SelectPersonalizedGifDialog } from "./SelectPersonalizedGifDialog";

export type PersonalizedGifFieldValue =
  | {
      personalizedGif: PersonalizedGif;
      campaignPersonalizedGif: null;
    }
  | {
      personalizedGif: null;
      campaignPersonalizedGif: CampaignPersonalizedGif;
    }
  | null;

const ScriptEditorContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
}));

const CAMPAIGN_INITIAL_SCRIPT_LABEL = "Initial Message Text";
const RESPONSE_SCRIPT_LABEL = "Script";

export function generateSampleContact({
  parsedTemplate,
  sampleContactData,
  personalizedGifContactFields,
}: {
  parsedTemplate: LiquidTemplate;
  sampleContactData: Record<string, string>;
  personalizedGifContactFields?: string[] | null;
}): Record<string, string> {
  const allFields = _.uniq([
    ...parsedTemplate.getRequiredAndAssignedVariables(),
    ...(personalizedGifContactFields ?? []),
  ]);
  const sampleContact: Record<string, string> = {};

  for (const field of allFields) {
    sampleContact[field] = sampleContactData[field] ?? `<example ${field}>`;
  }

  return sampleContact;
}

function getTemplateVariablesForGlobalResponseScript(
  parsedTemplate: LiquidTemplateParseResult | null
): string[] {
  // when we're editing a global response script, we don't have a campaign
  // to get contact fields from -- you can add whatever variables you want,
  // and we add those to the campaign when you import the script.
  //
  // when editing a script, we always make "first_name" and "last_name" available
  // since those are required in all campaigns. Additionally, if you're editing
  // an existing script, we show any variables you're already using.
  const templateVariables = parsedTemplate?.ok
    ? parsedTemplate.template.getRequiredAndAssignedVariables()
    : [];

  return uniq(templateVariables.concat(["first_name", "last_name"])).sort();
}

const LABELS_BY_TYPE = {
  campaignInitial: CAMPAIGN_INITIAL_SCRIPT_LABEL,
  response: RESPONSE_SCRIPT_LABEL,
  donation: "Message Text",
  keyword: "Message Text",
};

export const ScriptEditor = ({
  type,
  script,
  media,
  defaultAlert,
  campaign,
  fieldPrefix,
  linkShortenerDomainName,
  underGifLimit,
  hideContactFields = false,
  hidePreview = false,
  onSendPreviewMessage,
  sendPreviewDisabled,
}: {
  campaign?: CampaignForEditor;
  type: "campaignInitial" | "response" | "donation" | "keyword";
  script: string;
  media: MediaFieldValue;
  defaultAlert?: ReactNode;
  underGifLimit?: boolean; // only passed for initial scripts
  fieldPrefix?: string;
  linkShortenerDomainName?: string;
  hideContactFields?: boolean;
  hidePreview?: boolean;
  onSendPreviewMessage?: (opts: {
    contactData: Record<string, string>;
  }) => void;
  sendPreviewDisabled?: string | null;
}): ReactElement => {
  const label = LABELS_BY_TYPE[type];

  const getErrorMessage = useErrorMessageFormatter();
  const tenant = useTenant()!;

  const formik = useFormikContext();

  const { autoAddRefcodesToABLinks } = useSubaccountAdminConfig()!;

  const [parsedTemplate, setParsedTemplate] =
    useState<LiquidTemplateParseResult>(LiquidTemplate.fromString(script));

  const [createCampaignContactField] = useMutation<
    CreateCampaignContactFieldMutation,
    CreateCampaignContactFieldMutationVariables
  >(CREATE_CAMPAIGN_CONTACT_FIELD, {
    update: createCampaignContactFieldUpdater,
  });

  const [sampleContactData, setSampleContactData] = useState<
    Record<string, string>
  >(
    campaign?.sampleContact?.data
      ? formatContactData({
          contactData: JSON.parse(campaign.sampleContact.data),
          campaignContactFields: campaign.contactFields,
        }).formattedData
      : {}
  );

  const [globalResponseScriptVariables, setGlobalResponseScriptVariables] =
    useState<string[]>(
      campaign
        ? []
        : getTemplateVariablesForGlobalResponseScript(parsedTemplate)
    );

  const formikFieldPrefix = fieldPrefix ? `${fieldPrefix}.` : "";

  const personalizedGifField = formik.getFieldProps(
    `${formikFieldPrefix}personalizedGif`
  );
  const personalizedGifFieldValue: PersonalizedGifFieldValue =
    personalizedGifField.value;

  const hasAttachments = !!(media?.file || media?.upload);
  let renderedTemplate: React.ReactNode = null;
  let errorMessage: string | null = null;
  let encodingStats: EncodingStats | null = null;
  let sampleContact: Record<string, string> = {};

  if (parsedTemplate?.ok) {
    let personalizedGifContactFields: string[] = [];
    if (personalizedGifFieldValue?.campaignPersonalizedGif) {
      personalizedGifContactFields =
        personalizedGifFieldValue.campaignPersonalizedGif.creatomateTemplate.requiredContactFields.map(
          field => field.name
        );
    } else if (personalizedGifFieldValue?.personalizedGif) {
      personalizedGifContactFields =
        personalizedGifFieldValue.personalizedGif.creatomateTemplate.requiredContactFields.map(
          field => field.name
        );
    }

    sampleContact = generateSampleContact({
      parsedTemplate: parsedTemplate.template,
      sampleContactData,
      personalizedGifContactFields,
    });

    const renderResult = parsedTemplate.template.render(sampleContact, {
      refcode2w: "XXXXXX",
      autoAddRefcodesToABLinks,
    });

    if (renderResult.ok) {
      encodingStats = getEncodingStats(
        {
          text: renderResult.result,
          hasAttachments,
        },
        tenant
      );

      renderedTemplate = encodingStats.smartEncoded;

      // Extract URLs
      const textAndUrlSegments = parseTextAndUrlSegments(
        encodingStats.smartEncoded
      );

      if (linkShortenerDomainName) {
        // Recompute encoding stats
        const placeholderShortenedLink = `https://${linkShortenerDomainName}/${_.repeat(
          "x",
          6
        )}`;
        encodingStats = getEncodingStats(
          {
            text: replaceURLs(
              textAndUrlSegments,
              () => placeholderShortenedLink
            ).join(""),
            hasAttachments,
          },
          tenant
        );

        // display URLs as short links
        renderedTemplate = replaceURLs(textAndUrlSegments, url => (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {placeholderShortenedLink}
          </a>
        ));
      } else {
        // display URLs as un-transformed links
        renderedTemplate = replaceURLs(textAndUrlSegments, url => (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {url}
          </a>
        ));
      }
    } else {
      errorMessage = renderResult.error;
    }
  } else {
    errorMessage = "(invalid template)";
  }

  const onSampleContactChange = (update: { [field: string]: string }) => {
    setSampleContactData(prev => ({
      ...prev,
      ...update,
    }));
  };

  const contactFields =
    (campaign
      ? campaign.contactFields.map(field => ({
          id: field.id,
          name: field.name,
          isMapped: field.contactDataKey != null,
        }))
      : globalResponseScriptVariables.map(name => ({
          name,
          isMapped: true,
        }))) ?? [];

  const fieldsThatLookLikeUrls = _.reduce(
    sampleContactData,
    (acc, val, key) => (isValidUrl(val) ? acc.concat(key) : acc),
    [] as string[]
  );

  const contactSpecificMediaUrl =
    sampleContactData[
      formik.getFieldProps(`${formikFieldPrefix}mediaContactFieldName`).value
    ];
  const contactSpecificMedia = contactSpecificMediaUrl
    ? { url: contactSpecificMediaUrl }
    : null;
  const hasContactSpecificMedia = contactSpecificMedia ? true : false;
  const contactSpecificMediaFieldMeta = formik.getFieldMeta(
    `${formikFieldPrefix}mediaContactFieldName`
  );

  const personalizedGifsEnabled = useFeatureFlag("personalizedGifs");
  const contactSpecificMmsAttachmentsEnabled = useFeatureFlag(
    "mmsAttachmentUrlFromContact"
  );

  const hasValidMessagingStrategyForPersonalizedGifs =
    campaign && campaign.messagingStrategy
      ? VALID_PERSONALIZED_GIF_MESSAGING_STRATEGIES.includes(
          campaign.messagingStrategy.strategy
        )
      : false;
  const mediaField = formik.getFieldProps(`${formikFieldPrefix}media`);
  const mediaFieldMeta = formik.getFieldMeta(`${formikFieldPrefix}media`);
  const hasMedia =
    mediaField.value &&
    (mediaField.value.file || mediaField.value.upload || mediaField.value.url);
  const hasPersonalizedGif =
    !!personalizedGifFieldValue &&
    !!(
      personalizedGifFieldValue.personalizedGif ||
      personalizedGifFieldValue.campaignPersonalizedGif
    );

  const [personalizedGifDialogOpen, setPersonalizedGifDialogOpen] =
    useState(false);

  const [upload, setUpload] = useState<File>();
  const uploadTooLarge = upload && upload.size >= MEDIA_FILE_MAX_SIZE_BYTES;

  return (
    <ScriptEditorContainer>
      <FlexBox justifyContent="end">
        <SegmentEstimate encodingStats={encodingStats} />
      </FlexBox>
      <FormikScriptField
        label={label}
        secondaryLabel={
          <SegmentEstimateDetail
            encodingStats={encodingStats}
            hasAttachments={hasAttachments}
          />
        }
        name={`${formikFieldPrefix}script`}
        onParse={setParsedTemplate}
        hideContactFields={hideContactFields}
        contactFields={contactFields}
        onCreateContactField={
          campaign?.startedAt
            ? undefined
            : async (name: string) => {
                if (!campaign) {
                  setGlobalResponseScriptVariables(
                    uniq(globalResponseScriptVariables.concat([name]))
                  );
                } else {
                  const { errors } = await createCampaignContactField({
                    variables: {
                      input: {
                        campaignId: campaign.id,
                        name,
                      },
                    },
                  });

                  if (errors) {
                    throw new Error(getErrorMessage(errors));
                  }
                }
              }
        }
        media={
          hasMedia ? (
            <>
              <MediaPreview media={mediaField.value} />
              {mediaFieldMeta.error ? (
                <FormHelperText error>{mediaFieldMeta.error}</FormHelperText>
              ) : null}
            </>
          ) : hasContactSpecificMedia ? (
            <>
              <MediaPreview media={contactSpecificMedia} />
              {contactSpecificMediaFieldMeta.error ? (
                <FormHelperText error>
                  {contactSpecificMediaFieldMeta.error}
                </FormHelperText>
              ) : null}
            </>
          ) : hasPersonalizedGif ? (
            <CampaignPersonalizedGifPreview
              campaignId={campaign!.id}
              campaignPersonalizedGifId={
                personalizedGifFieldValue.campaignPersonalizedGif?.id
              }
              personalizedGifId={personalizedGifFieldValue.personalizedGif?.id}
              sx={{ height: "175px", width: "175px" }}
              pauseOnMouseOff={false}
            />
          ) : null
        }
        alert={
          (uploadTooLarge && (
            <Alert severity="error">
              <strong>{upload.name}</strong> is too large. Media files must be
              under {MEDIA_FILE_MAX_SIZE_BYTES / 1000} KB.
            </Alert>
          )) ||
          defaultAlert
        }
        additionalButtons={[
          hasMedia
            ? {
                value: "media",
                onClick: () => formik.setFieldValue(mediaField.name, null),
                children: "Remove Media File",
                selected: true,
              }
            : hasContactSpecificMedia
              ? {
                  value: "media",
                  onClick: () =>
                    formik.setFieldValue(
                      `${formikFieldPrefix}mediaContactFieldName`,
                      ""
                    ),
                  children: "Remove Media File",
                  selected: true,
                }
              : {
                  value: "media",
                  component: "label",
                  children: (
                    <>
                      <Upload />{" "}
                      {uploadTooLarge
                        ? "Replace Media File"
                        : "Upload a Media File"}
                      <input
                        type="file"
                        hidden
                        accept={MEDIA_FILE_MIME_TYPES}
                        onChange={async event => {
                          event.preventDefault();
                          if (!event.target.files?.length) {
                            return;
                          }
                          try {
                            const file = event.target.files[0];
                            setUpload(file);
                            if (file && file.size < MEDIA_FILE_MAX_SIZE_BYTES) {
                              await formik.setFieldValue(mediaField.name, {
                                file: file,
                              });
                            }
                            await formik.setFieldTouched(
                              mediaField.name,
                              true,
                              true
                            );
                          } catch (e) {
                            console.error(e);
                          }
                        }}
                      />
                    </>
                  ),
                  disabled: hasPersonalizedGif,
                },
          ...(campaign &&
          type === "campaignInitial" &&
          personalizedGifsEnabled &&
          hasValidMessagingStrategyForPersonalizedGifs &&
          (underGifLimit || hasPersonalizedGif)
            ? [
                hasPersonalizedGif
                  ? {
                      value: "personalizedGif",
                      children: "Remove Personalized GIF",
                      selected: true,
                      disabled:
                        campaign.personalizedGifStatus ===
                        PersonalizedGifStatus.Generated,
                      onClick: () =>
                        formik.setFieldValue(personalizedGifField.name, null),
                    }
                  : {
                      value: "personalizedGif",
                      children: "Add a Personalized GIF",
                      disabled:
                        hasMedia ||
                        hasContactSpecificMedia ||
                        campaign.personalizedGifStatus ===
                          PersonalizedGifStatus.None,
                      onClick: () => setPersonalizedGifDialogOpen(true),
                    },
              ]
            : []),
        ]}
      />
      {campaign && (
        <SelectPersonalizedGifDialog
          campaign={campaign}
          open={personalizedGifDialogOpen}
          onClose={() => setPersonalizedGifDialogOpen(false)}
          onSubmit={async personalizedGif => {
            await formik.setFieldValue(personalizedGifField.name, {
              personalizedGif,
              campaignPersonalizedGif: null,
            });
            setPersonalizedGifDialogOpen(false);
          }}
        />
      )}
      {type === "campaignInitial" && (
        <LinkShortenerSettings fieldPrefix={fieldPrefix} />
      )}
      {!!fieldsThatLookLikeUrls.length &&
        !hasAttachments &&
        !hasPersonalizedGif &&
        contactSpecificMmsAttachmentsEnabled && (
          <Box sx={{ mb: 1 }}>
            <FormikSelect
              sx={{ width: 1 }}
              label="MMS Import Column"
              bottomLabel="Select a column from your CSV upload to import as an MMS file"
              name={`${formikFieldPrefix}mediaContactFieldName`}
              withNoneOption={true}
              noneOptionLabel="Select a field..."
              variant="outlined"
            >
              {fieldsThatLookLikeUrls.map(fieldName => (
                <MenuItem key={fieldName} value={fieldName}>
                  {fieldName}
                </MenuItem>
              ))}
            </FormikSelect>
            <Alert
              severity="warning"
              sx={{ display: hasContactSpecificMedia ? "auto" : "none" }}
            >
              Media files can be a picture, video, GIF, or contact card. Files
              must be jpg, png, gif, mp4, or vcard format and under 500 KB.
              Attachments not meeting these guidelines will not be included in
              the message.
            </Alert>
          </Box>
        )}
      {parsedTemplate && !hidePreview && (
        <ScriptPreview
          campaignId={campaign?.id}
          media={contactSpecificMedia || media}
          personalizedGifId={personalizedGifFieldValue?.personalizedGif?.id}
          campaignPersonalizedGifId={
            personalizedGifFieldValue?.campaignPersonalizedGif?.id
          }
          renderedTemplate={renderedTemplate}
          errorMessage={errorMessage}
          sampleContact={sampleContact}
          onSampleContactChange={onSampleContactChange}
          onSendPreviewMessage={onSendPreviewMessage}
          sendPreviewDisabled={sendPreviewDisabled}
        />
      )}
    </ScriptEditorContainer>
  );
};
